import { useState, useEffect, useCallback, useRef } from "react";
import { Play, RefreshCw, Loader2, CheckCircle2, XCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

function adminReq(method, path, body) {
  const token = localStorage.getItem("cb_admin_token");
  return fetch(`${API}${path}`, {
    method: method.toUpperCase(),
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    ...(body ? { body: JSON.stringify(body) } : {}),
  }).then(r => r.json());
}

const DIVISIONS = [
  { label: "D1",   value: "Division I"  },
  { label: "D2",   value: "Division II" },
  { label: "NAIA", value: "NAIA"        },
  { label: "JUCO", value: "JUCO"        },
];

const defaultConfig = {
  divisions:      ["Division II"],
  max_colleges:   50,
  college_ids_raw: "",
  dry_run:        true,
  job_type:       "on_demand",
};

function formatDuration(secs) {
  if (!secs && secs !== 0) return "—";
  if (secs < 60) return `${secs}s`;
  return `${Math.floor(secs / 60)}m ${Math.round(secs % 60)}s`;
}

export default function AdminScraperPanel() {
  const [config, setConfig]           = useState(defaultConfig);
  const [confirmed, setConfirmed]     = useState(false);
  const [running, setRunning]         = useState(false);
  const [runResult, setRunResult]     = useState(null);
  const [runError, setRunError]       = useState(null);
  const [logs, setLogs]               = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);

  // Active-job tracking
  const [activeJobId, setActiveJobId] = useState(null);
  const [jobStatus, setJobStatus]     = useState(null);
  const pollRef = useRef(null);

  // ── Polling ────────────────────────────────────────────────────────────────
  const pollJob = useCallback(async (jobId) => {
    try {
      const data = await adminReq("get", `/admin/coach-lookup/jobs/${jobId}`);
      if (data.job) setJobStatus(data.job);
      if (data.is_complete) {
        clearInterval(pollRef.current);
        pollRef.current = null;
        setActiveJobId(null);
        setRunning(false);
        setTimeout(loadLogs, 1500);
      }
    } catch { /* ignore transient poll errors */ }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeJobId) {
      pollRef.current = setInterval(() => pollJob(activeJobId), 3000);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [activeJobId, pollJob]);

  // ── Logs ───────────────────────────────────────────────────────────────────
  const loadLogs = useCallback(async () => {
    setLogsLoading(true);
    try {
      const data = await adminReq("get", "/admin/coach-lookup/run-logs");
      setLogs(Array.isArray(data) ? data : []);
    } catch {}
    setLogsLoading(false);
  }, []);

  useEffect(() => { loadLogs(); }, [loadLogs]);

  // Reset confirmation whenever config changes
  useEffect(() => { setConfirmed(false); }, [config]);

  // ── Helpers ────────────────────────────────────────────────────────────────
  const toggleDivision = (div) => {
    setConfig(c => ({
      ...c,
      divisions: c.divisions.includes(div)
        ? c.divisions.filter(d => d !== div)
        : [...c.divisions, div],
    }));
  };

  const buildPayload = () => {
    const ids = config.college_ids_raw.split(",").map(s => s.trim()).filter(Boolean);
    return {
      job_type:     config.job_type,
      dry_run:      config.dry_run,
      divisions:    config.divisions.length ? config.divisions : null,
      max_colleges: Number(config.max_colleges),
      college_ids:  ids.length ? ids : null,
    };
  };

  const runLookup = async () => {
    if (!confirmed) return;
    setRunning(true);
    setRunError(null);
    setRunResult(null);
    setJobStatus(null);
    try {
      const res = await adminReq("post", "/admin/coach-lookup/run", buildPayload());
      if (res.detail) {
        setRunError(res.detail);
        setRunning(false);
      } else {
        setRunResult(res);
        setActiveJobId(res.job_id);
      }
    } catch (e) {
      setRunError("Run failed: " + e.message);
      setRunning(false);
    }
    setConfirmed(false);
  };

  // ── UI ─────────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">

      {/* ── Active job banner ─────────────────────────────────────────────── */}
      {activeJobId && (
        <div
          data-testid="running-job-banner"
          className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center gap-4"
        >
          <Loader2 className="w-5 h-5 text-blue-600 animate-spin shrink-0" />
          <div>
            <p className="text-sm font-semibold text-blue-800">
              AI lookup running
              {jobStatus && (
                <span className="ml-2 font-normal text-blue-600">
                  · {jobStatus.programmes_crawled || 0} colleges processed
                  · {(jobStatus.coaches_inserted || 0) + (jobStatus.coaches_updated || 0)} coaches found
                </span>
              )}
            </p>
            <p className="text-xs text-blue-500 mt-0.5 font-mono">{activeJobId}</p>
          </div>
        </div>
      )}

      {/* ── Configuration panel ───────────────────────────────────────────── */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-5">
        <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
          AI Coach Lookup — Configuration
        </h3>

        {/* Division checkboxes */}
        <div>
          <label className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2 block">
            Divisions
          </label>
          <div className="flex flex-wrap gap-2">
            {DIVISIONS.map(d => (
              <button
                key={d.value}
                data-testid={`div-toggle-${d.label}`}
                onClick={() => toggleDivision(d.value)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                  config.divisions.includes(d.value)
                    ? "bg-blue-600 text-white border-blue-600"
                    : "bg-white text-slate-600 border-slate-300 hover:border-blue-400"
                }`}
              >
                {d.label}
              </button>
            ))}
            <button
              data-testid="div-toggle-all"
              onClick={() => setConfig(c => ({ ...c, divisions: DIVISIONS.map(d => d.value) }))}
              className="px-3 py-1.5 rounded-lg text-sm font-medium border border-slate-300 text-slate-500 hover:border-slate-400 transition-colors"
            >
              All
            </button>
          </div>
        </div>

        {/* Max colleges + dry run */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1 block">
              Max colleges
            </label>
            <input
              data-testid="max-colleges-input"
              type="number" min="1" max="500"
              value={config.max_colleges}
              onChange={e => setConfig(c => ({ ...c, max_colleges: e.target.value }))}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <div className="flex flex-col justify-end pb-0.5">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                data-testid="dry-run-toggle"
                type="checkbox"
                checked={config.dry_run}
                onChange={e => setConfig(c => ({ ...c, dry_run: e.target.checked }))}
                className="w-4 h-4 rounded accent-amber-500"
              />
              <span className="text-sm text-slate-700">Dry run</span>
              <span className="text-xs text-slate-400">(no writes)</span>
            </label>
          </div>
        </div>

        {/* Specific college IDs */}
        <div>
          <label className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1 block">
            Specific college IDs{" "}
            <span className="font-normal normal-case">(comma-separated UUIDs — optional)</span>
          </label>
          <input
            data-testid="college-ids-input"
            type="text"
            placeholder="e.g. abc123, def456"
            value={config.college_ids_raw}
            onChange={e => setConfig(c => ({ ...c, college_ids_raw: e.target.value }))}
            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono"
          />
        </div>

        {/* Staging-only notice */}
        <p className="text-xs text-slate-400 bg-slate-50 rounded-lg px-3 py-2">
          Always writes to staging — live table is never touched by this tool.
        </p>

        {/* Confirm + Run */}
        <div className="border-t border-slate-100 pt-4 space-y-3">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              data-testid="confirm-run-checkbox"
              type="checkbox"
              checked={confirmed}
              onChange={e => setConfirmed(e.target.checked)}
              disabled={config.divisions.length === 0 || !!activeJobId}
              className="mt-0.5 w-4 h-4 rounded accent-blue-600"
            />
            <span className="text-sm text-slate-700">
              I confirm: run AI coach lookup on up to{" "}
              <strong>{config.max_colleges}</strong>{" "}
              college{Number(config.max_colleges) !== 1 ? "s" : ""}{" "}
              {config.dry_run
                ? <span className="text-amber-600 font-medium">(dry run — no writes)</span>
                : <span className="text-green-700 font-medium">and write results to staging</span>
              }.
            </span>
          </label>
          <button
            data-testid="run-scrape-btn"
            onClick={runLookup}
            disabled={!confirmed || running || !!activeJobId || config.divisions.length === 0}
            className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-40 transition-colors"
          >
            {running
              ? <><Loader2 className="w-4 h-4 animate-spin" /> Starting…</>
              : <><Play className="w-4 h-4" /> Run AI Coach Lookup</>
            }
          </button>
        </div>
      </div>

      {/* ── Run queued confirmation ────────────────────────────────────────── */}
      {runResult && (
        <div
          data-testid="run-result"
          className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-start gap-3"
        >
          <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-semibold text-green-800">Job started</p>
            <p className="text-xs text-green-700 mt-0.5">
              Job ID: <span className="font-mono">{runResult.job_id}</span> ·{" "}
              {runResult.colleges_queued} college{runResult.colleges_queued !== 1 ? "s" : ""} queued ·{" "}
              {runResult.dry_run ? "dry run" : "writing to staging"}
            </p>
          </div>
        </div>
      )}

      {runError && (
        <div
          data-testid="run-error"
          className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3"
        >
          <XCircle className="w-5 h-5 text-red-500 mt-0.5 shrink-0" />
          <p className="text-sm text-red-700">{runError}</p>
        </div>
      )}

      {/* ── Run logs ────────────────────────────────────────────────────────── */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <h3 className="text-sm font-semibold text-slate-700">Recent Lookup Runs</h3>
          <button
            data-testid="refresh-logs-btn"
            onClick={loadLogs}
            disabled={logsLoading}
            className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 transition-colors"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${logsLoading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>

        {logs.length === 0 ? (
          <p data-testid="no-logs-msg" className="text-sm text-slate-400 text-center py-8">
            {logsLoading ? "Loading…" : "No lookup runs recorded yet."}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200 text-xs text-slate-500 uppercase tracking-wide">
                <tr>
                  <th className="text-left px-4 py-2">Date</th>
                  <th className="text-left px-4 py-2">Divisions</th>
                  <th className="text-right px-4 py-2">Attempted</th>
                  <th className="text-right px-4 py-2">Added</th>
                  <th className="text-right px-4 py-2">Updated</th>
                  <th className="text-right px-4 py-2">Errors</th>
                  <th className="text-right px-4 py-2">Duration</th>
                  <th className="text-center px-4 py-2">Mode</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log, i) => (
                  <tr
                    key={log.id || i}
                    data-testid={`log-row-${i}`}
                    className="border-b border-slate-100 hover:bg-slate-50 transition-colors"
                  >
                    <td className="px-4 py-2 text-slate-600 whitespace-nowrap">
                      {new Date(log.run_date).toLocaleString("en-GB", {
                        day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
                      })}
                    </td>
                    <td className="px-4 py-2 text-slate-700">
                      {log.divisions_targeted ? log.divisions_targeted.join(", ") : "all"}
                    </td>
                    <td className="px-4 py-2 text-right text-slate-700">{log.colleges_attempted}</td>
                    <td className="px-4 py-2 text-right text-green-700">{log.coaches_added}</td>
                    <td className="px-4 py-2 text-right text-blue-600">{log.coaches_updated}</td>
                    <td className="px-4 py-2 text-right text-red-500">{log.errors}</td>
                    <td className="px-4 py-2 text-right text-slate-500">{formatDuration(log.run_duration_seconds)}</td>
                    <td className="px-4 py-2 text-center">
                      {log.dry_run
                        ? <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">dry</span>
                        : <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">live</span>
                      }
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
